/**
 * E05-F09 — "Test Prompt" window: pure (DOM-free, React-free) model.
 *
 * The DTOs now live in `@eer/shared` (`contracts/admin-prompt-runs.ts`), next to
 * the zod schemas the API validates its answers with, and are re-exported here
 * so the window keeps one import site. This file holds ONLY pure helpers; the
 * transport is `promptRunsApi.ts` → `real/promptRuns.ts`.
 *
 * THE HELPERS ARE A MIRROR OF THE SERVER, and that mirroring is the whole point
 * of the window (E05-F09-T02): the centre panel claims to show "the full prompt
 * that will be sent", so it has to be built by the same rules the server builds
 * it with — otherwise the tool lies in exactly the situations it exists to
 * diagnose.
 *
 *   - {@link substitutePromptVars} is a port of `substituteVariables()`
 *     (`packages/api/src/services/llm-prompt-loader.ts`), including its three
 *     non-obvious rules: it iterates the KEYS OF `vars` (so a per-placeholder
 *     default only applies to a key that IS in `vars`, with an empty value); the
 *     braces are matched EXACTLY (`{{ name }}` with spaces is NOT substituted);
 *     and a placeholder it cannot fill is left in the text VERBATIM.
 *   - {@link findMissingVars} therefore reads the drift OFF THE RENDERED STRING
 *     (shared `findUnfilledPromptVars`), not off `vars`/`defaults` — the same
 *     way `runPromptTest()` computes the `missingVars` it returns. "Has a value
 *     somewhere" and "was actually substituted" are not the same thing here.
 */
import {
  findUnfilledPromptVars,
  extractPromptPlaceholders,
  preKickoffAnalysisSchema,
} from '@eer/shared';
import type {
  PreKickoffAnalysis,
  PromptRunDetail,
  PromptRunSummary,
  PromptRunVar,
  PromptTestPreview,
  PromptTestPreviewItem,
  PromptTestResult,
} from '@eer/shared';

// ---------------------------------------------------------------------------
// DTOs — the wire contract, single-sourced from @eer/shared
// ---------------------------------------------------------------------------

export type {
  PromptRunOrigin,
  PromptRunSummary,
  PromptRunVar,
  PromptRunDetail,
  PromptTestResult,
  PromptTestPreview,
  PromptTestPreviewItem,
  PromptTestPreviewView,
} from '@eer/shared';

/**
 * Argument of the test mutation: the `.strict()` body of
 * `POST /admin/prompts/:promptKey/test` plus the key that travels in the path.
 * `template` omitted ⇒ the SAVED template is replayed (the window always sends
 * the draft, so that branch is the API's, not the window's).
 */
export interface PromptTestRequest {
  readonly promptKey: string;
  /** Which recorded run's `vars`/`defaults` to replay. */
  readonly runId: string;
  /** The DRAFT template from the window — never persisted by this call. */
  readonly template?: string;
}

// ---------------------------------------------------------------------------
// Placeholder substitution — mirror of the server's `substituteVariables()`
// ---------------------------------------------------------------------------

/**
 * Every `{{placeholder}}` in the template, de-duplicated, in first-use order.
 * Whitespace inside the braces is tolerated HERE (extraction) because the
 * seeded templates contain both forms and the admin must see both listed — but
 * see {@link substitutePromptVars}: the server only ever replaces the tight
 * form, so `{{ name }}` shows up as missing, which is the truth.
 */
export function extractPlaceholders(template: string): string[] {
  return extractPromptPlaceholders(template);
}

/** `vars` list → `{name: value}` map (the shape substitution works on). */
export function varsToMap(vars: readonly PromptRunVar[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const v of vars) map[v.name] = v.value;
  return map;
}

/** Escape a variable name for use inside a `RegExp` (the server does the same). */
function escapeForRegExp(name: string): string {
  return name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Build the prompt exactly as the server will: a PORT of `substituteVariables()`
 * (`llm-prompt-loader.ts`), loop for loop.
 *
 * Parity rules, each of which the previous (prototype) implementation got wrong
 * and each of which is visible in the window:
 *   1. iteration is over `values`, NOT over the placeholders of the template —
 *      so a `defaults` entry whose key is absent from the run does nothing and
 *      the placeholder survives into the sent text;
 *   2. an empty / whitespace-only recorded value falls back to `defaults[key]`
 *      ('(No email)', 'Unknown', 'not specified' …), not to the empty string;
 *   3. the match is `{{key}}` with no inner whitespace.
 *
 * The replacement is the string form of `String.replace`, again for parity: the
 * server passes the value as a string too, so a value containing `$&` renders
 * identically here and there rather than "more correctly" in the preview.
 */
export function substitutePromptVars(
  template: string,
  values: Readonly<Record<string, string>>,
  defaults: Readonly<Record<string, string>> = {},
): string {
  let out = String(template ?? '');
  for (const [key, raw] of Object.entries(values)) {
    let value = String(raw ?? '');
    if (value.trim() === '') value = '';
    if (value === '' && key in defaults) value = defaults[key] ?? '';
    out = out.replace(new RegExp(`\\{\\{${escapeForRegExp(key)}\\}\\}`, 'g'), value);
  }
  return out;
}

/**
 * The placeholders of the draft that SURVIVED substitution — i.e. the ones this
 * run cannot fill. This is the spec's "variable drift" warning, and it is
 * deliberately derived from the rendered string rather than from the data:
 * `substituteVariables()` leaves such a placeholder in the text verbatim, so the
 * model literally receives `{{roleName}}` and answers around it. Unnamed, that
 * reads as the prompt misbehaving.
 *
 * Same computation as the server's `missingVars` (both call the shared
 * `findUnfilledPromptVars`), so the warning shown BEFORE the paid call and the
 * one returned WITH the result cannot disagree.
 */
export function findMissingVars(template: string, renderedPrompt: string): string[] {
  return findUnfilledPromptVars(template, renderedPrompt);
}

/**
 * Variables the run recorded but the draft no longer uses. The other half of
 * drift — harmless for the call, but it tells the admin that the run's data
 * and the template have grown apart.
 */
export function findUnusedVars(template: string, vars: readonly PromptRunVar[]): string[] {
  const used = new Set(extractPlaceholders(template));
  return vars.map((v) => v.name).filter((name) => !used.has(name));
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** `12 693 chars` / `12.4 KB` — the size shown next to each variable. */
export function formatChars(chars: number): string {
  if (chars < 1024) return `${chars} chars`;
  return `${(chars / 1024).toFixed(1)} KB`;
}

/** `1.8 s` for anything over a second, `840 ms` below it. */
export function formatDuration(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`;
}

/**
 * The same, for a duration that MAY BE ABSENT. A journal row written before the
 * timing column existed (or by a path that failed before the call returned) has
 * `durationMs: null`, and the comparison panel has to say "not recorded" rather
 * than invent `0 ms` — the whole point of the side-by-side is that the numbers
 * next to each answer are that answer's own.
 */
export function formatDurationOrDash(ms: number | null): string {
  return ms === null ? '—' : formatDuration(ms);
}

/**
 * Token counts as one line: `1240 in / 830 out`.
 *
 * Both `null` ⇒ 'not reported', SAID OUT LOUD rather than rendered as an empty
 * gap: the providers this app calls do not return usage on every path today, and
 * a blank next to "Tokens" reads as "this call was free".
 */
export function formatTokens(input: number | null, output: number | null): string {
  if (input === null && output === null) return 'not reported';
  return `${input ?? '—'} in / ${output ?? '—'} out`;
}

/** `29.07 14:03` — the compact stamp used in the run selector. */
export function formatRunStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** One run's outcome, as a short word for the selector line and the badges. */
export type PromptRunStatus = 'ok' | 'parse-failed' | 'error';

export function runStatus(run: PromptRunSummary): PromptRunStatus {
  if (run.hasError) return 'error';
  if (run.parseOk === false) return 'parse-failed';
  return 'ok';
}

const STATUS_LABEL: Record<PromptRunStatus, string> = {
  ok: 'ok',
  'parse-failed': 'parse failed',
  error: 'call failed',
};

export function runStatusLabel(run: PromptRunSummary): string {
  return STATUS_LABEL[runStatus(run)];
}

/**
 * The selector line from the spec:
 * `project:170 · 29.07 14:03 · claude-haiku-4-5 · ok` — with `[test]` in front
 * for a replay, so a test run can never be mistaken for production material.
 */
export function runLabel(run: PromptRunSummary): string {
  const where = run.entityRef ?? 'no entity';
  const prefix = run.origin === 'test' ? '[test] ' : '';
  return `${prefix}${where} · ${formatRunStamp(run.createdAt)} · ${run.model} · ${runStatusLabel(run)}`;
}

// ---------------------------------------------------------------------------
// E05-F09-T03 — one classification for BOTH answers (test call & recorded run)
// ---------------------------------------------------------------------------

/**
 * The three facts an answer panel has to render, whichever side it is on: the
 * text the model produced, the error that replaced it, and whether the standard
 * parser could read it.
 */
export interface PromptAnswerInput {
  /** The model's answer before parsing; `null` when there is none stored. */
  readonly text: string | null;
  /** Set when the CALL failed (429 / 529 / timeout) — then `text` is null. */
  readonly error: string | null;
  /** Tri-state: `null` ⇒ parsing does not apply (a plain-text prompt). */
  readonly parseOk: boolean | null;
  /** Why the parse failed, when the producer of this answer said so. */
  readonly parseError?: string | null;
}

/**
 * What the panel must actually show. Every "nothing to see" case is its own
 * named state, because the acceptance of this task is precisely that a failed
 * parse — and an empty answer — are shown AS SUCH and never as a blank panel:
 * an empty box is exactly the symptom the admin opened this window to explain.
 */
export type PromptAnswerState =
  | { readonly kind: 'call-failed'; readonly error: string }
  /** Nothing was recorded at all and no error was recorded either. */
  | { readonly kind: 'no-answer' }
  /** The call succeeded and the model returned nothing (or only whitespace). */
  | { readonly kind: 'empty-answer' }
  /** There IS text; the standard parser could not read it. */
  | { readonly kind: 'parse-failed'; readonly text: string; readonly parseError: string | null }
  | { readonly kind: 'answer'; readonly text: string };

/**
 * Classify one answer. Shared by the "Raw answer" tab (the test call) and the
 * "What production got" tab (the replayed run) so the two sides of the
 * comparison cannot describe the same situation differently.
 *
 * Order matters and is deliberate:
 *   1. a failed call is reported as a failed call, not as an empty answer;
 *   2. `null` (nothing stored) and `''` (the model answered with nothing) are
 *      DIFFERENT findings — the first is a gap in the journal, the second is a
 *      real, reproducible defect of the prompt;
 *   3. emptiness is decided before the parse verdict: a whitespace-only answer
 *      does have `parseOk === false`, but "the model said nothing" is the
 *      useful sentence, not "the parser could not read it".
 */
export function describeAnswer(answer: PromptAnswerInput): PromptAnswerState {
  if (answer.error !== null && answer.error !== '') {
    return { kind: 'call-failed', error: answer.error };
  }
  if (answer.text === null) return { kind: 'no-answer' };
  if (answer.text.trim() === '') return { kind: 'empty-answer' };
  if (answer.parseOk === false) {
    return { kind: 'parse-failed', text: answer.text, parseError: answer.parseError ?? null };
  }
  return { kind: 'answer', text: answer.text };
}

/**
 * Model / tokens / time of ONE call. Both sides of the comparison carry their
 * own set: the test call ran now, on today's model, and the recorded run ran
 * whenever it ran on whatever model was configured then — showing the test's
 * numbers above production's answer would be a lie in the one place the admin
 * is looking for the difference.
 */
export interface PromptCallStats {
  readonly provider: string;
  readonly model: string;
  readonly inputTokens: number | null;
  readonly outputTokens: number | null;
  readonly durationMs: number | null;
}

/** Stats of the test call that has just been made. */
export function testCallStats(result: PromptTestResult): PromptCallStats {
  return {
    provider: result.provider,
    model: result.model,
    inputTokens: result.inputTokens,
    outputTokens: result.outputTokens,
    durationMs: result.durationMs,
  };
}

/** Stats of the recorded run being replayed (the "what production got" side). */
export function recordedRunStats(run: PromptRunDetail): PromptCallStats {
  return {
    provider: run.provider,
    model: run.model,
    inputTokens: run.inputTokens,
    outputTokens: run.outputTokens,
    durationMs: run.durationMs,
  };
}

// ---------------------------------------------------------------------------
// E05-F09-T04 — "How the user will see it"
// ---------------------------------------------------------------------------

/**
 * The prompt → renderer decision USED TO LIVE HERE, as a hand-kept map of prompt
 * keys to `DocumentView` doc types. It no longer does, and that is the point of
 * T04: what the user sees is not the raw parse but the answer after the
 * PRODUCTION pipeline's own parse and cleanup, and every pipeline cleans up
 * differently (`extractJsonLoose` + the recruiter signature for the document
 * package, a zod shape for the notification, fence-stripping + a JSON shape for
 * the salary line whose `output_format` says "plain text"). Re-deciding that in
 * the browser would drift from the server the moment a pipeline changes — the
 * exact failure mode this window exists to catch.
 *
 * So the server ships `result.preview` (`buildPromptTestPreview`, built by
 * calling the production functions themselves) and this file only helps DRAW it.
 */

/**
 * The analysis item's content, validated. The pre-kickoff brief is rendered from
 * the same view model the live Pre-Kickoff panel uses
 * (`buildPreKickoffBrief` / `buildAnalysisDetail`), and those take a typed
 * `PreKickoffAnalysis` — this is the one gate between "unknown from the wire"
 * and that call. A shape that does not validate returns `null` and the caller
 * falls back to the structured listing rather than rendering half a brief.
 */
export function asPreKickoffAnalysis(content: unknown): PreKickoffAnalysis | null {
  const parsed = preKickoffAnalysisSchema.safeParse(content);
  return parsed.success ? parsed.data : null;
}

/**
 * Label for the item picker when one call produced several pieces (four
 * documents; the analysis plus three drafts). Falls back to the doc type so a
 * server that adds a piece without a label never renders a nameless tab.
 */
export function previewItemLabel(item: PromptTestPreviewItem, index: number): string {
  const label = item.label.trim();
  if (label !== '') return label;
  return item.docType.trim() !== '' ? item.docType : `Result ${index + 1}`;
}

/**
 * WHAT the "As the user sees it" tab must draw for a call that produced an
 * answer. Pure, so the decision is testable without a DOM — and because it is
 * the one decision this tab can get wrong in a way that silently un-does the
 * whole feature (see below).
 *
 *   - `preview`    — the answer after THIS prompt's production parse + cleanup
 *                    (`buildPromptTestPreview` server-side), split into the
 *                    pieces the live screens draw. The normal case.
 *   - `parsed`     — no preview came back (an older server), but the generic
 *                    parse did produce something: fields beat an empty panel.
 *   - `raw`        — neither: the answer verbatim, still never an empty box.
 *   - `empty`      — nothing at all to show.
 *
 * `parseOk` DELIBERATELY plays no part here. It is the verdict of the GENERIC
 * JSON parser (tri-state: `null` = this prompt's `output_format` does not
 * promise JSON at all — `search_salary_rate` says "plain text, single line"),
 * whereas the preview is built by the PIPELINE's own cleanup, which for that
 * very prompt does parse `{found,salary,quote,note}` and composes the line the
 * questionnaire receives. Gating the preview on `parseOk !== null` — as this tab
 * did until E05-F09-T04's e2e caught it — made every plain-text-declared prompt
 * render the uncleaned model text in BOTH the "As the user sees it" and the
 * "Raw answer" tabs, i.e. exactly the comparison this window exists to provide,
 * collapsed into one.
 */
export type UserTabContent =
  | { readonly kind: 'preview'; readonly preview: PromptTestPreview }
  | { readonly kind: 'parsed'; readonly parsed: unknown }
  | { readonly kind: 'raw'; readonly text: string }
  | { readonly kind: 'empty' };

export function chooseUserTabContent(
  result: Pick<PromptTestResult, 'preview' | 'parsed' | 'rawText'>,
): UserTabContent {
  const preview = result.preview;
  // An empty `items` list is not a preview: `UserResultView` would render its
  // "the cleanup read nothing renderable" sentence and drop the answer, while
  // the parse / raw text below is still something the admin can judge.
  if (preview && preview.items.length > 0) return { kind: 'preview', preview };
  if (result.parsed !== null && result.parsed !== undefined) {
    return { kind: 'parsed', parsed: result.parsed };
  }
  const raw = result.rawText ?? '';
  return raw.trim() === '' ? { kind: 'empty' } : { kind: 'raw', text: raw };
}

/**
 * Prompts whose call goes out to the web. Such a test costs more and runs for
 * minutes rather than seconds, so the window badges them (spec § UI).
 */
const WEB_SEARCH_PROMPT_KEYS: ReadonlySet<string> = new Set([
  'search_salary_rate',
  'search_website_for_missing',
  'research_verify_text',
  'research_verify_about_us',
  'research_discover',
  'research_enrich_company',
]);

export function usesWebSearch(promptKey: string): boolean {
  return WEB_SEARCH_PROMPT_KEYS.has(promptKey);
}

/**
 * Pretty-print a parsed answer for the structured fallback. Kept here (not in
 * the component) so the "no dedicated view" panel is testable without a DOM.
 */
export function formatParsed(parsed: unknown): string {
  if (parsed === null || parsed === undefined) return '';
  if (typeof parsed === 'string') return parsed;
  try {
    return JSON.stringify(parsed, null, 2);
  } catch {
    return String(parsed);
  }
}

/** Top-level `key → value` pairs of a parsed object, for the structured list. */
export function parsedEntries(parsed: unknown): { key: string; value: string }[] {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
  return Object.entries(parsed as Record<string, unknown>).map(([key, value]) => ({
    key,
    value: typeof value === 'string' ? value : formatParsed(value),
  }));
}
